import {
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { Modal } from "antd"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { IFeatureAnnouncement } from "services/featureAnnouncementsService"
import { useWhatsNewStore } from "stores/whats-new"
import { AnnouncementMedia } from "./AnnouncementMedia"
import {
  isInAppLink,
  toPlainText,
  useMarkFeatureAnnouncementSeen,
  useUnseenFeatureAnnouncements,
} from "./useWhatsNew"

type Props = {
  /** The What's New screen for this layout, e.g. "/dashboard/whats-new". */
  whatsNewPath: string
}

/**
 * The chip and close button sit over the media, so they stay out of the way while a
 * video plays: shown on hover, while paused, with keyboard focus inside the media, and
 * always on touch screens, which have no hover.
 */
const overMediaClassName = (paused: boolean) =>
  `absolute top-4 z-[1] transition-opacity duration-200 ${
    paused
      ? "opacity-100"
      : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 [.group:has(:focus-visible)_&]:pointer-events-auto [.group:has(:focus-visible)_&]:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"
  }`

const arrowClassName =
  "flex h-9 w-9 items-center justify-center rounded-full text-neutral400 transition-colors hover:bg-neutral20 hover:text-neutral700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 disabled:pointer-events-none disabled:opacity-30"

/**
 * Shown once, right after a real sign-in, when the user has unseen announcements: one
 * slide per announcement. A slide is marked seen when its "Learn more" is followed (its
 * CTA, or the post on the What's New screen when it has none) or its video is played.
 * Closing the popup or just viewing a slide marks nothing.
 */
export const WhatsNewPopup = ({ whatsNewPath }: Props) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [mediaPaused, setMediaPaused] = useState(true)
  const handlePausedChange = useCallback(
    (paused: boolean) => setMediaPaused(paused),
    []
  )
  const { showAfterLogin, clearAfterLogin } = useWhatsNewStore()
  const { data: unseen = [], isFetched } = useUnseenFeatureAnnouncements()
  const markSeen = useMarkFeatureAnnouncementSeen()
  // The slides as they were when the popup opened. Marking one seen refreshes the
  // unseen list, which must not reshuffle the slides under the user.
  const [slides, setSlides] = useState<IFeatureAnnouncement[]>([])

  useEffect(() => {
    if (!showAfterLogin || !isFetched) return
    // One chance per sign-in, whether or not there was anything to show.
    clearAfterLogin()
    if (unseen.length > 0) {
      setSlides(unseen)
      setIndex(0)
      setOpen(true)
    }
  }, [showAfterLogin, isFetched, unseen, clearAfterLogin])

  const total = slides.length
  const item: IFeatureAnnouncement | undefined =
    slides[Math.min(index, total - 1)]

  // Closing is "not now", not "seen": the popup comes back at the next sign-in until
  // each post is read or its video played.
  const dismiss = () => setOpen(false)

  const learnMore = () => {
    if (!item) return
    setOpen(false)

    // No CTA: open this post on the What's New screen, which marks it seen there.
    if (!item.cta_link) {
      navigate(`${whatsNewPath}?id=${item.id}`)
      return
    }

    markSeen(item)
    if (isInAppLink(item.cta_link)) {
      navigate(item.cta_link)
    } else {
      window.open(item.cta_link, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <Modal
      open={open && !!item}
      onCancel={dismiss}
      footer={null}
      closable={false}
      centered
      width={600}
      wrapClassName="[&_.ant-modal-content]:!p-0 [&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:overflow-hidden"
    >
      {item && (
        <>
          <div className="group relative aspect-video w-full bg-[#F3F5FA]">
            {total > 1 && (
              <span
                className={`${overMediaClassName(
                  mediaPaused
                )} left-4 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-primary500 shadow-sm`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary500" />
                {index + 1} of {total} updates
              </span>
            )}
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className={`${overMediaClassName(
                mediaPaused
              )} right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral700 shadow-sm hover:bg-neutral20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500`}
            >
              <XMarkIcon className="w-5 stroke-2" />
            </button>

            <AnnouncementMedia
              key={item.id}
              item={item}
              onPausedChange={handlePausedChange}
              onEngage={() => markSeen(item)}
            />
          </div>

          <div className="flex flex-col px-4 pb-6 pt-7">
            <h2 className="text-xl font-bold leading-tight text-padeBlack">
              {item.title}
            </h2>
            <p className="mt-3 line-clamp-3 text-sm font-medium leading-relaxed text-padeSubtext">
              {toPlainText(item.content)}
            </p>

            {total > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {unseen.map((slide, slideIndex) => (
                    <button
                      key={slide.id}
                      type="button"
                      onClick={() => setIndex(slideIndex)}
                      aria-label={`Show update ${slideIndex + 1}`}
                      aria-current={slideIndex === index}
                      className={`h-2 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 ${
                        slideIndex === index
                          ? "w-8 bg-primary500"
                          : "w-2 bg-neutral40 hover:bg-neutral60"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setIndex((value) => value - 1)}
                    disabled={index === 0}
                    aria-label="Previous update"
                    className={arrowClassName}
                  >
                    <ArrowLeftIcon className="w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIndex((value) => value + 1)}
                    disabled={index >= total - 1}
                    aria-label="Next update"
                    className={arrowClassName}
                  >
                    <ArrowRightIcon className="w-5" />
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={learnMore}
              className="mt-6 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary500 text-sm font-semibold text-white transition-colors hover:bg-primary700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 focus-visible:ring-offset-2"
            >
              {item.cta_link ? item.cta_text || "Learn more" : "Learn more"}
              <ArrowRightIcon className="w-4 stroke-2" />
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="mt-3 self-center rounded px-2 py-1 text-sm text-neutral300 transition-colors hover:text-neutral600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500"
            >
              Dismiss for now
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}
